import { useTranslations } from 'next-intl'
import { Control, Controller } from 'react-hook-form'
import { Download, FileSpreadsheet, FileText, X } from 'lucide-react'
import Icon from '@/shared/v2/shared/components/Icon'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import UploadFile from '@/shared/v2/shared/components/UploadFile'
import { parseFileInfo } from '@/shared/utils/filesUtils'
import { WorkflowConfig } from '../models/importModels'
import { FormValues } from './NewImportDrawer'

type WorkflowDetailsProps = {
    selectedConfig: WorkflowConfig
    control: Control<FormValues>
}

const WorkflowDetails = ({ selectedConfig, control }: WorkflowDetailsProps) => {
    const t = useTranslations('imports')

    return (
        <div className="grid grid-cols-1 gap-6" data-testid="__new-import-workflow-details__">
            {selectedConfig.description && (
                <Paragraph color="darkGrey" data-testid="__new-import-description__">
                    {selectedConfig.description}
                </Paragraph>
            )}

            {selectedConfig.templateUrl && (
                <a
                    href={selectedConfig.templateUrl}
                    download
                    className="inline-flex items-center gap-1 text-textLink text-base font-medium underline underline-offset-[3px] transition-all duration-300 border-b-borderPositive hover:no-underline"
                    data-testid="__new-import-template-link__"
                >
                    <Icon icon={Download} size="1" />
                    {t('newImport.template')}
                </a>
            )}

            <Controller
                name="files"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => {
                    const csvFileInfo = value?.[0] ? parseFileInfo({ file: value[0] }) : null
                    return (
                        <div className="grid grid-cols-1 gap-2">
                            <Paragraph variant="medium" size={3}>
                                {t('newImport.file.label')}
                            </Paragraph>

                            {value?.[0] ? (
                                <div className="border border-borderPrimary rounded-sm">
                                    <div className="flex items-center gap-2 py-1.5 px-3">
                                        <Icon icon={FileSpreadsheet} size="1" className="text-textPositive shrink-0" />
                                        <Paragraph size={3} className="truncate flex-1 min-w-0">
                                            {value[0].name}
                                        </Paragraph>
                                        {csvFileInfo && (
                                            <Paragraph size={3} color="darkGrey" className="shrink-0">
                                                {csvFileInfo.size.value} {csvFileInfo.size.unit.toUpperCase()}
                                            </Paragraph>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => onChange({ target: { value: undefined } })}
                                            className="shrink-0"
                                            aria-label={t('newImport.actions.removeCsvFile')}
                                            data-testid="__csv-file-remove__"
                                        >
                                            <Icon icon={X} size="1" color="negative" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <UploadFile
                                    accept={{ 'text/csv': ['.csv'] }}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    value={value || null}
                                    hideFileList
                                />
                            )}
                        </div>
                    )
                }}
            />

            <Controller
                name="assetFiles"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                    <div className="grid grid-cols-1 gap-2">
                        <Paragraph variant="medium" size={3}>
                            {t.has(`newImport.assets.label.${selectedConfig.workflowType}`)
                                ? t(`newImport.assets.label.${selectedConfig.workflowType}`)
                                : t('newImport.assets.label.default')}
                        </Paragraph>

                        <UploadFile
                            accept={{ 'application/pdf': ['.pdf'] }}
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value || null}
                            hideFileList
                        />

                        <Paragraph size={3} color="darkGrey">
                            {t.has(`newImport.assets.description.${selectedConfig.workflowType}`)
                                ? t(`newImport.assets.description.${selectedConfig.workflowType}`)
                                : t('newImport.assets.description.default')}
                        </Paragraph>

                        {value && value.length > 0 && (
                            <div className="border border-borderPrimary rounded-sm">
                                <div className="flex items-center justify-between px-3 py-2 bg-backgroundSecondary border-b border-borderPrimary">
                                    <Paragraph variant="medium" size={3}>
                                        {t('newImport.assets.count', { count: value.length })}
                                    </Paragraph>
                                    <button
                                        type="button"
                                        onClick={() => onChange({ target: { value: [] } })}
                                        className="text-sm text-textNegative hover:underline"
                                        data-testid="__new-import-clear-assets__"
                                    >
                                        {t('newImport.assets.clear')}
                                    </button>
                                </div>
                                <div className="max-h-[176px] overflow-y-auto">
                                    {value.map((file, index) => {
                                        const fileInfo = parseFileInfo({ file })
                                        return (
                                            <div
                                                key={index}
                                                className="flex items-center gap-2 py-1.5 px-3 border-b border-borderPrimary last:border-b-0"
                                            >
                                                <Icon
                                                    icon={FileText}
                                                    size="1"
                                                    className="text-textInformation shrink-0"
                                                />
                                                <Paragraph size={3} className="truncate flex-1 min-w-0">
                                                    {file.name}
                                                </Paragraph>
                                                {fileInfo && (
                                                    <Paragraph size={3} color="darkGrey" className="shrink-0">
                                                        {fileInfo.size.value} {fileInfo.size.unit.toUpperCase()}
                                                    </Paragraph>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const remaining = value.filter((_, i) => i !== index)
                                                        onChange({
                                                            target: {
                                                                value: remaining.length > 0 ? remaining : undefined
                                                            }
                                                        })
                                                    }}
                                                    className="shrink-0"
                                                    aria-label={t('newImport.actions.removeAssetFile')}
                                                    data-testid={`__asset-file-remove-${index}__`}
                                                >
                                                    <Icon icon={X} size="1" color="negative" />
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            />
        </div>
    )
}

export default WorkflowDetails
